import { Button, InlineSpinner, VisualList, VisualListItem } from "@vector-im/compound-web";
import CheckIcon from "@vector-im/compound-design-tokens/assets/web/icons/check";
import InfoIcon from "@vector-im/compound-design-tokens/assets/web/icons/info";
import ErrorIcon from "@vector-im/compound-design-tokens/assets/web/icons/error-solid";
import React, { type JSX, useState } from "react";

import { _t } from "../../../../languageHandler";
import { EncryptionCard } from "./EncryptionCard";
import { uiAuthCallback } from "../../../../CreateCrossSigning";
import { EncryptionCardButtons } from "./EncryptionCardButtons";
import { EncryptionCardEmphasisedContent } from "./EncryptionCardEmphasisedContent";
import { useMatrixClientContext } from "../../../../contexts/MatrixClientContext";
import { SKIP_CROSS_SIGNING_BOOTSTRAP } from "../../../../dth/constants";
import { logger as baseLogger } from "matrix-js-sdk/src/logger";

const logger = baseLogger.getChild("ResetIdentityBody");

interface ResetIdentityBodyProps {
    /**
     * Called when the identity is reset.
     */
    onReset: () => void;

    /**
     * Called when the cancel button is clicked.
     */
    onCancelClick: () => void;

    /**
     * The variant of the panel to show. We show more warnings in the 'compromised' variant (no use in showing a user
     * this warning if they have to reset because they no longer have their key)
     */
    variant: ResetIdentityBodyVariant;
}

/**
 * The variant of the panel to show.  This affects the message displayed to the user.
 *
 * "compromised" is shown when the user chose 'Reset cryptographic identity' explicitly in settings, usually because
 * they believe their identity has been compromised.
 *
 * "sync_failed" is shown when the user tried to recover their identity but the process failed, probably because
 * the required information is missing from recovery.
 *
 * "forgot" is shown when the user chose 'Forgot recovery key?' during `SetupEncryptionToast`.
 *
 * "confirm" is shown when the user chose 'Reset all' during `SetupEncryptionBody`.
 */
export type ResetIdentityBodyVariant = "compromised" | "forgot" | "sync_failed" | "confirm";

/**
 * User interface component allowing the user to reset their cryptographic identity.
 *
 * Used by {@link ResetIdentityPanel}.
 */
export function ResetIdentityBody({ onCancelClick, onReset, variant }: ResetIdentityBodyProps): JSX.Element {
    const matrixClient = useMatrixClientContext();

    // After the user clicks "Continue", we disable the button so it can't be
    // clicked again, and warn the user not to close the window.
    const [inProgress, setInProgress] = useState(false);

    return (
        <EncryptionCard Icon={ErrorIcon} destructive={true} title={titleForVariant(variant)}>
            <EncryptionCardEmphasisedContent>
                <VisualList>
                    <VisualListItem Icon={CheckIcon} success={true}>
                        {_t("settings.encryption.advanced.breadcrumb_first_description")}
                    </VisualListItem>
                    <VisualListItem Icon={InfoIcon}>
                        {_t("settings.encryption.advanced.breadcrumb_second_description")}
                    </VisualListItem>
                    <VisualListItem Icon={InfoIcon}>
                        {_t("settings.encryption.advanced.breadcrumb_third_description")}
                    </VisualListItem>
                </VisualList>
                {variant === "compromised" && <span>{_t("settings.encryption.advanced.breadcrumb_warning")}</span>}
            </EncryptionCardEmphasisedContent>
            <EncryptionCardButtons>
                <Button
                    destructive={true}
                    disabled={inProgress}
                    onClick={async () => {
                        setInProgress(true);

                        const crypto = matrixClient.getCrypto();
                        if (crypto != null) {
                            if (SKIP_CROSS_SIGNING_BOOTSTRAP) {
                                // Disables server-side key storage and deletes server-side backups:
                                //  - Deletes the current key backup version, if any (but not any previous versions).
                                //  - Disables 4S, deleting the info for the default key, the default key pointer itself and any known 4S data (cross-signing keys and the megolm key backup key).
                                //  - Deletes any dehydrated devices.
                                //  - Sets the "m.org.matrix.custom.backup_disabled" account data flag to indicate that the user has disabled backups.
                                // Disabilita lo storage server-side (cancella il backup attivo e i dehydrated devices)
                                await crypto.disableKeyStorage();

                                // Se esistono backup attivi, li cancella tutti.
                                let lastVersion = null;
                                let version = (await crypto.getKeyBackupInfo())?.version ?? null;
                                while (version != null) {
                                    if (lastVersion === version) {
                                        logger.error("Key backup version did not change after deletion, aborting to avoid infinite loop");
                                        break;
                                    }
                                    lastVersion = version;
                                    await crypto.deleteKeyBackupVersion(version);
                                    version = (await crypto.getKeyBackupInfo())?.version ?? null;
                                }

                                logger.debug("All key backup versions deleted");

                                // Non esiste un metodo pubblico per cancellare TUTTI i dati di secret storage;
                                // se vuoi ricreare un secret storage nuovo dovresti usare `bootstrapSecretStorage`
                                // con `setupNewSecretStorage: true` e fornire una nuova chiave tramite `createSecretStorageKey`.

                                // Reset cross-signing (crea nuove chiavi)
                                // await crypto.bootstrapCrossSigning({ setupNewCrossSigning: true, authUploadDeviceSigningKeys });

                                // Crea un nuovo key backup
                                await crypto.resetKeyBackup();
                            } else {
                                await crypto.resetEncryption((makeRequest) => uiAuthCallback(matrixClient, makeRequest));
                            }
                        }

                        onReset();
                    }}
                >
                    {inProgress ? (
                        <>
                            <InlineSpinner /> {_t("settings.encryption.advanced.reset_in_progress")}
                        </>
                    ) : (
                        _t("action.continue")
                    )}
                </Button>
                {inProgress ? (
                    <EncryptionCardEmphasisedContent>
                        <span className="mx_ResetIdentityPanel_warning">
                            {_t("settings.encryption.advanced.do_not_close_warning")}
                        </span>
                    </EncryptionCardEmphasisedContent>
                ) : (
                    <Button kind="tertiary" onClick={onCancelClick}>
                        {_t("action.cancel")}
                    </Button>
                )}
            </EncryptionCardButtons>
        </EncryptionCard>
    );
}

function titleForVariant(variant: ResetIdentityBodyVariant): string {
    switch (variant) {
        case "compromised":
        case "confirm":
            return _t("settings.encryption.advanced.breadcrumb_title");
        case "sync_failed":
            return _t("settings.encryption.advanced.breadcrumb_title_sync_failed");
        case "forgot":
            return _t("settings.encryption.advanced.breadcrumb_title_forgot");
    }
}
